import { 
  signInWithEmailAndPassword, 
  signOut, 
  createUserWithEmailAndPassword,
  UserCredential
} from "firebase/auth";
import { auth, db } from "../firebase/firebase";
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  getDocs, 
  query, 
  where, 
  updateDoc, 
  deleteDoc,
  orderBy,
  limit 
} from "firebase/firestore";

// User login
export const loginUser = async (email: string, password: string): Promise<UserCredential> => {
  return signInWithEmailAndPassword(auth, email, password);
};

// User logout
export const logoutUser = async (): Promise<void> => {
  return signOut(auth);
};

// Admin creates a new user
export const createUser = async (
  email: string, 
  password: string, 
  userData: { 
    name: string, 
    role: string, 
    createdBy: string 
  }
): Promise<void> => {
  // Create the user in Firebase Authentication
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;
  
  // Store additional user data in Firestore
  await setDoc(doc(db, "users", user.uid), {
    email: user.email,
    name: userData.name,
    role: userData.role,
    createdBy: userData.createdBy,
    createdAt: new Date(),
    uid: user.uid,
    status: 'active'
  });

  // Sign out immediately (since admin is creating the account, not the user)
  return signOut(auth);
};

// Check if user is admin
export const checkIsAdmin = async (uid: string): Promise<boolean> => {
  const userDoc = await getDoc(doc(db, "users", uid));
  if (userDoc.exists()) {
    const userData = userDoc.data();
    return userData.role === "admin";
  }
  return false;
};

// Get user data
export const getUserData = async (uid: string) => {
  const userDoc = await getDoc(doc(db, "users", uid));
  if (userDoc.exists()) {
    return userDoc.data();
  }
  return null;
};

// Get all users
export const getUsers = async (searchTerm = '', pageSize = 10) => {
  try {
    // Start with basic query
    let usersQuery = query(
      collection(db, "users"),
      orderBy("name"),
      limit(pageSize)
    );
    
    // If search term provided, filter by name or email
    if (searchTerm) {
      // Note: Firebase doesn't support OR queries directly, so this is simplified
      // For a real app, you might need to do multiple queries and combine results
      usersQuery = query(
        collection(db, "users"),
        where("name", ">=", searchTerm),
        where("name", "<=", searchTerm + "\uf8ff"),
        limit(pageSize)
      );
    }
    
    const querySnapshot = await getDocs(usersQuery);
    
    const users: any[] = [];
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return users;
  } catch (error) {
    console.error("Error getting users:", error);
    throw error;
  }
};

// Update user data
export const updateUserData = async (uid: string, userData: { name?: string, role?: string, status?: string }) => {
  try {
    await updateDoc(doc(db, "users", uid), userData);
    return true;
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};

// Delete a user
export const deleteUserAccount = async (uid: string) => {
  try {
    // Delete from Firestore first
    await deleteDoc(doc(db, "users", uid));
    
    // Note: For Firebase Auth deletion, you need to be signed in as that user or use admin SDK
    // This is a limitation of client-side Firebase without custom backend
    // In a real application, you'd implement this in a secure cloud function
    console.log("User document deleted from Firestore. Auth user would require backend functions to delete.");
    
    return true;
  } catch (error) {
    console.error("Error deleting user:", error);
    throw error;
  }
}; 